/**
 * Tax calculation utilities for the frontend
 * Mirrors the backend PricingService calculations to provide immediate UI feedback
 */

const DEFAULT_TAX_RATE = 0.13; // 13%
const PRICING_MODE_TAX_EXCLUSIVE = 'tax_exclusive';
const PRICING_MODE_TAX_INCLUSIVE = 'tax_inclusive';

/**
 * Round to 2 decimal places for financial accuracy
 */
function roundToTwoDecimals(value) {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/**
 * Calculate all pricing data from base price, pricing mode, and tax rate
 */
export function calculatePricingData(basePrice, pricingMode = PRICING_MODE_TAX_EXCLUSIVE, taxRate = DEFAULT_TAX_RATE) {
  try {
    const basePriceFloat = parseFloat(basePrice) || 0;
    const taxRateFloat = parseFloat(taxRate) || DEFAULT_TAX_RATE;
    
    // Validate pricing mode
    const validPricingMode = [PRICING_MODE_TAX_EXCLUSIVE, PRICING_MODE_TAX_INCLUSIVE].includes(pricingMode) 
      ? pricingMode 
      : PRICING_MODE_TAX_EXCLUSIVE;

    let taxExclusivePrice, taxInclusivePrice;

    if (validPricingMode === PRICING_MODE_TAX_INCLUSIVE) {
      // Base price is tax-inclusive, calculate tax-exclusive
      taxInclusivePrice = basePriceFloat;
      taxExclusivePrice = basePriceFloat / (1 + taxRateFloat);
    } else {
      // Base price is tax-exclusive, calculate tax-inclusive
      taxExclusivePrice = basePriceFloat;
      taxInclusivePrice = basePriceFloat * (1 + taxRateFloat);
    }

    // Round to 2 decimal places
    taxExclusivePrice = roundToTwoDecimals(taxExclusivePrice);
    taxInclusivePrice = roundToTwoDecimals(taxInclusivePrice);

    const taxAmount = roundToTwoDecimals(taxInclusivePrice - taxExclusivePrice);

    return {
      basePrice: basePriceFloat,
      pricingMode: validPricingMode,
      taxRate: taxRateFloat,
      taxExclusivePrice,
      taxInclusivePrice,
      taxAmount,
      formattedTaxRate: `${(taxRateFloat * 100).toFixed(1)}%`
    };
  } catch (error) {
    console.error('Error calculating pricing data:', error);
    // Return safe defaults on error
    return {
      basePrice: parseFloat(basePrice) || 0,
      pricingMode: PRICING_MODE_TAX_EXCLUSIVE,
      taxRate: DEFAULT_TAX_RATE,
      taxExclusivePrice: parseFloat(basePrice) || 0,
      taxInclusivePrice: roundToTwoDecimals((parseFloat(basePrice) || 0) * 1.13),
      taxAmount: roundToTwoDecimals((parseFloat(basePrice) || 0) * 0.13),
      formattedTaxRate: "13.0%"
    };
  }
}

/**
 * Calculate tax-exclusive price from tax-inclusive price
 */
export function calculateTaxExclusiveFromInclusive(taxInclusivePrice, taxRate = DEFAULT_TAX_RATE) {
  const inclusive = parseFloat(taxInclusivePrice) || 0;
  const rate = parseFloat(taxRate) || DEFAULT_TAX_RATE;
  return roundToTwoDecimals(inclusive / (1 + rate));
}

/**
 * Calculate tax-inclusive price from tax-exclusive price
 */
export function calculateTaxInclusiveFromExclusive(taxExclusivePrice, taxRate = DEFAULT_TAX_RATE) {
  const exclusive = parseFloat(taxExclusivePrice) || 0;
  const rate = parseFloat(taxRate) || DEFAULT_TAX_RATE;
  return roundToTwoDecimals(exclusive * (1 + rate));
}

/**
 * Validate pricing data
 */
export function validatePricingData(data) {
  const errors = {};
  
  // Validate base_price
  const basePrice = data.basePrice || data.base_price;
  if (basePrice === null || basePrice === undefined) {
    errors.basePrice = 'Base price is required';
  } else if (isNaN(parseFloat(basePrice)) || parseFloat(basePrice) < 0) {
    errors.basePrice = 'Base price must be a non-negative number';
  }
  
  // Validate pricing_mode
  const pricingMode = data.pricingMode || data.pricing_mode;
  if (pricingMode && ![PRICING_MODE_TAX_EXCLUSIVE, PRICING_MODE_TAX_INCLUSIVE].includes(pricingMode)) {
    errors.pricingMode = `Pricing mode must be one of: ${PRICING_MODE_TAX_EXCLUSIVE}, ${PRICING_MODE_TAX_INCLUSIVE}`;
  }
  
  // Validate tax_rate
  const taxRate = data.taxRate || data.tax_rate;
  if (taxRate !== null && taxRate !== undefined) {
    const rate = parseFloat(taxRate);
    if (isNaN(rate) || rate < 0 || rate > 1) {
      errors.taxRate = 'Tax rate must be a decimal between 0 and 1 (e.g., 0.13 for 13%)';
    }
  }
  
  return errors;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount, currency = 'CNY') {
  try {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  } catch (error) {
    return `¥${(amount || 0).toFixed(2)}`;
  }
}

/**
 * Format percentage for display
 */
export function formatPercentage(rate) {
  return `${((rate || 0) * 100).toFixed(1)}%`;
}

/**
 * Calculate totals for multiple items
 */
export function calculateTotals(items, documentPricingMode = PRICING_MODE_TAX_EXCLUSIVE) {
  if (!Array.isArray(items) || items.length === 0) {
    return {
      subtotalTaxExclusive: 0,
      subtotalTaxInclusive: 0,
      totalTaxAmount: 0,
      subtotal: 0,
      totalPrice: 0
    };
  }

  let subtotalTaxExclusive = 0;
  let subtotalTaxInclusive = 0;

  items.forEach(item => {
    const quantity = parseFloat(item.quantity) || 1;
    const pricingData = calculatePricingData(
      item.basePrice || item.base_price || item.price,
      item.pricingMode || item.pricing_mode || documentPricingMode,
      item.taxRate || item.tax_rate
    );

    subtotalTaxExclusive += pricingData.taxExclusivePrice * quantity;
    subtotalTaxInclusive += pricingData.taxInclusivePrice * quantity;
  });

  subtotalTaxExclusive = roundToTwoDecimals(subtotalTaxExclusive);
  subtotalTaxInclusive = roundToTwoDecimals(subtotalTaxInclusive);
  const totalTaxAmount = roundToTwoDecimals(subtotalTaxInclusive - subtotalTaxExclusive);

  // Primary totals based on document pricing mode
  const subtotal = documentPricingMode === PRICING_MODE_TAX_INCLUSIVE ? subtotalTaxInclusive : subtotalTaxExclusive;
  
  return {
    subtotalTaxExclusive,
    subtotalTaxInclusive,
    totalTaxAmount,
    subtotal,
    totalPrice: subtotal // Same as subtotal for now, can be extended for discounts, etc.
  };
}

export const TaxCalculator = {
  calculatePricingData,
  calculateTaxExclusiveFromInclusive,
  calculateTaxInclusiveFromExclusive,
  validatePricingData,
  formatCurrency,
  formatPercentage,
  calculateTotals,
  PRICING_MODE_TAX_EXCLUSIVE,
  PRICING_MODE_TAX_INCLUSIVE,
  DEFAULT_TAX_RATE
};